import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
  sideOffset?: number;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  delay = 0.2,
  sideOffset = 80
}) => {
  const [isVisible, setIsVisible] = useState(false);
  let timeoutId: number;

  const handleMouseEnter = () => {
    timeoutId = window.setTimeout(() => {
      setIsVisible(true);
    }, delay * 1000);
  };

  const handleMouseLeave = () => {
    clearTimeout(timeoutId);
    setIsVisible(false);
  };

  const getPosition = () => {
    switch (side) {
      case 'top':
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
      case 'right':
        return 'left-full top-1/2 -translate-y-1/2 ml-2';
      case 'bottom':
        return 'top-full left-1/2 -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 -translate-y-1/2 mr-2';
    }
  };

  const getArrowPosition = () => {
    switch (side) {
      case 'top':
        return 'bottom-[-4px] left-1/2 -translate-x-1/2 border-t-background border-x-transparent border-b-transparent';
      case 'right':
        return 'left-[-4px] top-1/2 -translate-y-1/2 border-r-background border-y-transparent border-l-transparent';
      case 'bottom':
        return 'top-[-4px] left-1/2 -translate-x-1/2 border-b-background border-x-transparent border-t-transparent';
      case 'left':
        return 'right-[-4px] top-1/2 -translate-y-1/2 border-l-background border-y-transparent border-r-transparent';
    }
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 px-3 py-1.5 text-xs font-medium bg-background border rounded-md shadow-md whitespace-nowrap ${getPosition()}`}
          >
            {content}
            <span className={`absolute border-[4px] ${getArrowPosition()}`} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Tooltip;